import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import AppButton from "../common/buttons/AppButton";
import CustomTypography from "../common/form/CustomTypography";
import {
  PodCatalogVariantDrawerRow,
  PodCatalogVariantUpsert,
  buildPodVariantSku,
} from "../../../models/Products/PodCatalog";
import AppSearchDropdownMulti from "../common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { useStores } from "../../../use-stores";

interface PodProductVariantsDrawerProps {
  open: boolean;
  productName?: string;
  productId?: number;
  podCatalogueId?: number;
  isLoading?: boolean;
  initialRows?: PodCatalogVariantUpsert[];
  canEditVariants?: boolean;
  onClose: () => void;
  onSubmitAll: (rows: PodCatalogVariantUpsert[]) => Promise<void> | void;
}

const VARIANT_DRAWER_Z_OFFSET = 2000;

const PodProductVariantsDrawer: React.FC<PodProductVariantsDrawerProps> = ({
  open,
  productName,
  productId,
  podCatalogueId,
  isLoading = false,
  initialRows = [],
  canEditVariants = true,
  onClose,
  onSubmitAll,
}) => {
  const theme = useTheme();
  const { podCatalogStore } = useStores();
  const rows = podCatalogStore.detailVariantDrawerRows;
  const savingErrors = podCatalogStore.detailVariantSavingErrors;
  const deletingErrors = podCatalogStore.detailVariantDeletingErrors;
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [copiedSkuRowId, setCopiedSkuRowId] = useState<string | null>(null);

  const sizeOptionList = useMemo<FilterValue[]>(
    () =>
      [
        "XXS",
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "XXL",
        "3XL",
        "4XL",
        "5XL",
        "FREE SIZE",
        ...rows.map((row) => row.sku?.trim() || ""),
      ]
        .filter(Boolean)
        .filter((size, idx, arr) => arr.indexOf(size) === idx)
        .map((size, index) => ({
          id: String(index + 1),
          value: size,
        })),
    [rows]
  );

  useEffect(() => {
    if (!open) return;
    podCatalogStore.initDetailVariantDrawerFromRows(initialRows);
  }, [open, initialRows, podCatalogStore]);

  const hasRows = rows.length > 0;
  const totalVariants = rows.filter((row) => !row.isEditing).length;

  const generateVariantSku = (row: PodCatalogVariantDrawerRow) => {
    if (!productId || !row.sku?.trim()) return undefined;
    return buildPodVariantSku(productId, row.sku, row.color);
  };

  const getAccentColor = (row: PodCatalogVariantDrawerRow) => {
    const fallback = "#6366F1";
    const candidate = (row.color || "").trim();
    if (!candidate) return fallback;
    try {
      // CSS.supports exists in modern browsers; fallback if unavailable.
      // If the color string isn't valid, we use the fallback.
      // eslint-disable-next-line no-undef
      if (typeof CSS !== "undefined" && CSS.supports("color", candidate)) {
        return candidate;
      }
    } catch (_e) {}
    return fallback;
  };

  const handleCopySku = async (rowId: string) => {
    const row = rows.find((r) => r.rowId === rowId);
    if (!row) return;
    const sku = generateVariantSku(row);
    if (!sku) return;
    try {
      await navigator.clipboard.writeText(sku);
      setCopiedSkuRowId(rowId);
      setTimeout(() => {
        setCopiedSkuRowId((current) => (current === rowId ? null : current));
      }, 1200);
    } catch (_error) {}
  };

  const normalizeRowsForSubmit = (
    sourceRows: PodCatalogVariantDrawerRow[]
  ): PodCatalogVariantUpsert[] =>
    sourceRows.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      color: row.color?.trim() || undefined,
      sku: row.sku?.trim() || undefined,
      variant_sku: generateVariantSku(row) || undefined,
    }));

  const handleAddVariantRow = () => {
    if (!canEditVariants) return;
    podCatalogStore.addDetailVariantDrawerRow();
  };

  const handleRowChange = (
    rowId: string,
    key: keyof PodCatalogVariantUpsert,
    value: string | number
  ) => {
    podCatalogStore.updateDetailVariantDrawerRow(rowId, key, value);
  };

  const handleSaveRow = async (rowId: string) => {
    if (!canEditVariants) return;
    const targetRow = rows.find((row) => row.rowId === rowId);
    if (!targetRow) return;
    setSavingRowId(rowId);
    podCatalogStore.clearDetailVariantSavingError(rowId);
    try {
      const nextRows = rows.map((row) =>
        row.rowId === rowId ? { ...row, isEditing: false } : row
      );
      await onSubmitAll(normalizeRowsForSubmit(nextRows));
      podCatalogStore.setDetailVariantDrawerRows(nextRows);
    } catch (err: any) {
      podCatalogStore.setDetailVariantSavingError(
        rowId,
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save variant. Please try again."
      );
    } finally {
      setSavingRowId(null);
    }
  };

  const handleDeleteRow = async (rowId: string) => {
    if (!canEditVariants) return;
    podCatalogStore.clearDetailVariantDeletingError(rowId);
    const nextRows = rows.filter((row) => row.rowId !== rowId);
    try {
      await onSubmitAll(normalizeRowsForSubmit(nextRows));
      podCatalogStore.setDetailVariantDrawerRows(nextRows);
    } catch (err: any) {
      podCatalogStore.setDetailVariantDeletingError(
        rowId,
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete variant. Please try again."
      );
    }
  };

  const handleCancelEdit = (rowId: string) => {
    podCatalogStore.cancelDetailVariantRowEdit(rowId);
  };

  const cards = useMemo(
    () =>
      rows.map((row) => {
        if (row.isEditing) {
          const canSave = !!row.sku?.trim();
          const generatedSku = generateVariantSku(row);
          const saveError = savingErrors[row.rowId];
          return (
            <Box
              key={row.rowId}
              sx={{
                backgroundColor: "#FFFFFF",
                border: "1.5px solid #6366F1",
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow: "0 0 0 3px rgba(99,102,241,0.08)",
              }}
            >
              <Box sx={{ px: 2, pt: 2, pb: 1.5, display: "grid", gap: 1.5 }}>
                <Box>
                  <CustomTypography variant="body3" fontWeight={600} color="#64748B" sx={{ mb: 0.5, display: "block" }}>
                    Color*
                  </CustomTypography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. black"
                    value={row.color || ""}
                    onChange={(e) => handleRowChange(row.rowId, "color", e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "& input": { py: 0.9, fontSize: "0.82rem" },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <CustomTypography variant="body3" fontWeight={600} color="#64748B" sx={{ mb: 0.5, display: "block" }}>
                      Size *
                    </CustomTypography>
                    <AppSearchDropdownMulti
                      isMultiSelect={false}
                      title="Select size"
                      list={sizeOptionList}
                      value={
                        row.sku
                          ? sizeOptionList.filter((option) => option.value === row.sku)
                          : []
                      }
                      local={true}
                      handleSelect={(selectedItems) =>
                        handleRowChange(row.rowId, "sku", selectedItems[0]?.value || "")
                      }
                      inputHeight="38px"
                      borderRadius="10px"
                      minWidth="100%"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CustomTypography variant="body3" fontWeight={600} color="#64748B" sx={{ mb: 0.5, display: "block" }}>
                      SKU Preview
                    </CustomTypography>
                    <TextField
                      size="small"
                      fullWidth
                      value={generatedSku || ""}
                      placeholder="Auto generated"
                      InputProps={{ readOnly: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "& input": { py: 0.9, fontSize: "0.82rem" },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {saveError && (
                <Box
                  sx={{
                    mx: 2,
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    backgroundColor: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: "8px",
                  }}
                >
                  <CustomTypography
                    variant="body3"
                    sx={{ color: "#DC2626", fontWeight: 500 }}
                  >
                    {saveError}
                  </CustomTypography>
                </Box>
              )}

              <Box sx={{ px: 2, pb: 1.75, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <AppButton
                  label="Cancel"
                  variant="secondary"
                  onClick={() => handleCancelEdit(row.rowId)}
                  customStyles={{
                    minHeight: "34px",
                    minWidth: "80px",
                    borderRadius: "8px",
                    fontSize: "0.78rem",
                    backgroundColor: "#F1F5F9",
                    color: "#475569",
                    border: "1px solid #E2E8F0",
                    boxShadow: "none",
                  }}
                />
                <AppButton
                  label={savingRowId === row.rowId ? "Saving..." : "Save Variant"}
                  startIcon={
                    savingRowId === row.rowId ? (
                      <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />
                    ) : (
                      <CheckCircleOutlineIcon sx={{ fontSize: "16px !important" }} />
                    )
                  }
                  onClick={() => handleSaveRow(row.rowId)}
                  disabled={
                    !canEditVariants || !canSave || savingRowId === row.rowId
                  }
                  customStyles={{
                    minHeight: "34px",
                    minWidth: "120px",
                    borderRadius: "8px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    background: "linear-gradient(90deg, #6366F1 0%, #4F46E5 100%)",
                    color: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(79,70,229,0.25)",
                  }}
                />
              </Box>
            </Box>
          );
        }

        return (
          <Box
            key={row.rowId}
            sx={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderLeft: `3px solid ${getAccentColor(row)}`,
              borderRadius: "10px",
              overflow: "hidden",
              transition: "box-shadow 0.15s",
              "&:hover": { boxShadow: "0 2px 12px rgba(15,23,42,0.08)" },
            }}
          >
            {/* Row 1: Size + Color + Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                pt: 1.5,
                pb: 0.75,
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  height: 26,
                  px: 1.25,
                  borderRadius: "6px",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  backgroundColor: "#EEF2FF",
                  color: "#4F46E5",
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                {(row.sku || "–").toUpperCase()}
              </Box>

              <Box
                sx={{
                  width: "1px",
                  height: 20,
                  backgroundColor: "#E2E8F0",
                  flexShrink: 0,
                }}
              />

              <CustomTypography
                variant="body2"
                sx={{
                  color: row.color?.trim() ? "#334155" : "#94A3B8",
                  fontWeight: row.color?.trim() ? 500 : 400,
                }}
                style={{ fontSize: "0.8rem" }}
              >
                {(row.color || "No color").toUpperCase()}
              </CustomTypography>

              <Box sx={{ flex: 1 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={!canEditVariants}
                  onClick={() =>
                    podCatalogStore.setDetailVariantRowEditing(row.rowId, true)
                  }
                  sx={{
                    color: "#64748B",
                    "&:hover": { backgroundColor: "#EEF2FF", color: "#4F46E5" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!canEditVariants}
                  onClick={() => handleDeleteRow(row.rowId)}
                  sx={{
                    color: "#64748B",
                    "&:hover": {
                      backgroundColor: "#FEF2F2",
                      color: "#DC2626",
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>

            {/* SKU strip */}
            <Box
              sx={{
                px: 2,
                py: 0.9,
                backgroundColor: "#FFFFFF",
                borderTop: "1px solid #F1F5F9",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <CustomTypography
                variant="body3"
                color="#94A3B8"
                sx={{ whiteSpace: "nowrap", fontSize: "0.7rem" }}
              >
                SKU
              </CustomTypography>
              <CustomTypography
                variant="body3"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.71rem",
                  color: "#475569",
                  letterSpacing: "0.02em",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={generateVariantSku(row) || ""}
              >
                {generateVariantSku(row) || "–"}
              </CustomTypography>
              <Tooltip title={copiedSkuRowId === row.rowId ? "Copied!" : "Copy SKU"}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleCopySku(row.rowId)}
                    disabled={!generateVariantSku(row)}
                    sx={{
                      color:
                        copiedSkuRowId === row.rowId ? "#16A34A" : "#94A3B8",
                      p: 0.4,
                    }}
                  >
                    <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Delete error */}
            {!!deletingErrors[row.rowId] && (
              <Box sx={{ px: 2, pb: 1, pt: 0.25 }}>
                <CustomTypography
                  variant="body3"
                  sx={{ color: "#DC2626", fontWeight: 500 }}
                >
                  {deletingErrors[row.rowId]}
                </CustomTypography>
              </Box>
            )}
          </Box>
        );
      }),
    [
      rows,
      savingRowId,
      savingErrors,
      deletingErrors,
      productId,
      copiedSkuRowId,
    ]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + VARIANT_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + VARIANT_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 600, md: 680 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + VARIANT_DRAWER_Z_OFFSET,
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : "#F8FAFC",
        },
      }}
    >
      <Box
        sx={{
          px: 2.75,
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E2E8F0",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              backgroundColor: "#EEF2FF",
              color: "#4F46E5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading6" fontWeight={700} style={{ marginBottom: "4px" }}>
              POD Variant Options
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors.greyTextColor}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: { xs: "76vw", sm: 460 },
              }}
            >
              {productName || "-"}
            </CustomTypography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#94A3B8",
            "&:hover": { backgroundColor: "#F1F5F9", color: "#334155" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {isLoading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomTypography variant="body2" fontWeight={700} color="#334155">
                Variants
              </CustomTypography>
              <Box
                sx={{
                  height: 20,
                  px: 1,
                  borderRadius: "999px",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  backgroundColor: "#EEF2FF",
                  color: "#4F46E5",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {totalVariants}
              </Box>
            </Box>
            {canEditVariants && (
            <AppButton
              label="Add Variant"
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={handleAddVariantRow}
              customStyles={{
                minWidth: "132px",
                minHeight: "36px",
                borderRadius: "10px",
                fontSize: "0.78rem",
                backgroundColor: "#FDF2F8",
                color: "#DB2777",
                border: "1px solid #FBCFE8",
                boxShadow: "none",
              }}
            />
            )}
          </Box>

          {!hasRows ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 1,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 36, color: "#CBD5E1" }} />
              <CustomTypography variant="body2" color="#94A3B8">
                {canEditVariants
                  ? "No variants yet. Click Add Variant to get started."
                  : "No variants configured."}
              </CustomTypography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>{cards}</Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default observer(PodProductVariantsDrawer);
